import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import { useMetricPattern } from '../../charts/performance-chart/funcs';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { serverFilterId, timeRangeFilterId } from '../../filters';

const title = t`Server Memory Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metricPatterns: ['mem:usage:'],
    },
};

const metricPatternMapping = [
    {
        pattern: 'mem:usage:',
        trendOptions: {
            formatter: FORMATTER_TYPES.PERCENT,
            units: '%',
            forceDecimals: true,
        },
    },
];

const serverMemUtilChartConfig = {
    yAxis: [
        {
            title: {
                text: t`Utilization (%)`,
            },
            ceiling: 100,
        },
    ],
    tooltip: BuiltInTooltip,
    showGranularity: true,
};

function ServerMemoryUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const firstData = data?.data?.[0];
    const chartTrends = useMetricPattern(metricPatternMapping, firstData);
    const chartDef = useMemo(() => {
        return { ...serverMemUtilChartConfig, chartTrends };
    }, [chartTrends]);

    if (!data) return null;

    return <PerformanceChart chartDef={chartDef} data={firstData} />;
}

ServerMemoryUtilizationPanel.propTypes = panelPropTypes;

ServerMemoryUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'ServerMemUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SERVER,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Memory utilization trend for a selected server`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, serverFilterId],
        optional: [],
    },
})(ServerMemoryUtilizationPanel);
